import { useState, useEffect, useCallback } from "react";
import { useLoggedUser } from "../../utils/hooks/useLoggedUser";

const PAGINATION_HEIGHT = 70;
const ROW_HEIGHT = 50;
const NON_TABLE_OFFSET = 217;

const getHeaderHeight = () => document.querySelector(".layout-header")?.offsetHeight || 68;

const computeStaticHeight = (footerHeight) =>
  Math.max(window.innerHeight - getHeaderHeight() - NON_TABLE_OFFSET - footerHeight, 200);

export const useTableFullHeight = (containerRef) => {
  const loggedUser = useLoggedUser();
  const companyFooter = loggedUser?.company?.footer;
  const footerHeight = companyFooter ? 42 : 0;

  const [tableConfig, setTableConfig] = useState(() => ({
    height: computeStaticHeight(footerHeight),
    pageSize: 10
  }));

  const computeHeight = useCallback(() => {
    if (!containerRef?.current) return { height: computeStaticHeight(footerHeight), pageSize: 10 };

    const container = containerRef.current;
    const containerTop = container.getBoundingClientRect().top;

    const filtersElement = container.querySelector(".table-outer-filters");
    const dynamicFiltersHeight = filtersElement ? filtersElement.offsetHeight : 0;

    const availableHeight =
      window.innerHeight - containerTop - dynamicFiltersHeight - PAGINATION_HEIGHT - footerHeight - getHeaderHeight();
    const finalHeight = Math.max(availableHeight, 200);
    const suggestedPageSize = Math.floor((finalHeight - 40) / ROW_HEIGHT);
    const pageSize = Math.max(suggestedPageSize, 10);

    return { height: finalHeight, pageSize };
  }, [containerRef, footerHeight]);

  // Resize listener handles both zoom and device rotation (landscape ↔ portrait)
  useEffect(() => {
    const handleResize = () => {
      setTableConfig(computeHeight());
    };

    if (containerRef?.current) {
      const observer = new ResizeObserver(handleResize);
      observer.observe(document.documentElement);
      handleResize();
      return () => observer.disconnect();
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [computeHeight, containerRef]);

  return tableConfig;
};
